import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { smsApi } from "../../../api/resources";
import type { SegmentInfo, TemplateInfo } from "../../../api/types";
import { Button, ErrorText } from "../../../components/ui";

function Counts({ info, testId }: { info: SegmentInfo; testId: string }) {
  return (
    <p data-testid={testId} className={`text-xs ${info.over_one_segment ? "text-warn" : "text-zinc-500"}`}>
      {info.characters} chars · {info.segments} segment{info.segments === 1 ? "" : "s"} · {info.encoding}
      {info.over_one_segment && " · over 160 chars / 1 segment, costs more per text"}
    </p>
  );
}

export function TemplateCard({ t, onSaved }: { t: TemplateInfo; onSaved: (t: TemplateInfo) => void }) {
  const [body, setBody] = useState(t.body);
  const [preview, setPreview] = useState<(SegmentInfo & { preview: string }) | null>(t);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = body !== t.body;
  const id = t.key.toLowerCase().replace(/_/g, "-");

  useEffect(() => setBody(t.body), [t.body]);
  useEffect(() => {
    const h = setTimeout(() => {
      smsApi.previewTemplate(t.key, body).then((p) => { setPreview(p); setError(null); }).catch((e) => setError(errorMessage(e)));
    }, 350);
    return () => clearTimeout(h);
  }, [body, t.key]);

  const run = async (fn: () => Promise<TemplateInfo>, msg: string) => {
    setBusy(true);
    try {
      onSaved(await fn());
      toast.success(msg);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid={`template-card-${id}`} className="rounded-sm border border-white/10 bg-surface p-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="font-mono text-sm font-semibold text-white">{t.key}</h3>
        {t.documentation_only && <span data-testid={`template-doc-only-${id}`} className="rounded-sm border border-white/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-400">Documentation only</span>}
      </div>
      <p className="mb-3 text-xs text-zinc-400">{t.description}</p>
      {t.hint && <p data-testid={`template-hint-${id}`} className="mb-3 rounded-sm border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-100">{t.hint}</p>}
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={640} data-testid={`template-body-${id}`}
        className="w-full rounded-sm border border-white/15 bg-ink px-3 py-2 font-mono text-xs text-white focus:border-signal focus:outline-none" />
      <div className="mt-3 rounded-sm border border-white/10 bg-ink/60 p-3">
        <p className="label-caps mb-1 text-zinc-500">Live preview (sample values)</p>
        <p data-testid={`template-preview-${id}`} className="text-sm text-zinc-200">{preview?.preview}</p>
        {preview && <div className="mt-2"><Counts info={preview} testId={`template-counts-${id}`} /></div>}
      </div>
      <div className="mt-2"><ErrorText message={error} testId={`template-error-${id}`} /></div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button className="h-9 px-4 text-[11px]" disabled={!dirty || !!error} loading={busy} onClick={() => run(() => smsApi.saveTemplate(t.key, body), "Template saved")} data-testid={`template-save-${id}`}>Save</Button>
        <Button variant="ghost" className="h-9 px-3 text-[11px]" disabled={t.body === t.default_body && !dirty} onClick={() => run(() => smsApi.resetTemplate(t.key), "Template reset to default")} data-testid={`template-reset-${id}`}>
          <RotateCcw className="h-3.5 w-3.5" /> Reset to default
        </Button>
      </div>
    </article>
  );
}
